/**
 * InputManager类
 * 管理用户输入（键盘和触摸）
 */

import { Direction } from './types';
import { KEY_CODES } from './constants';

type EventCallback = (direction: Direction) => void;

export class InputManager {
  private events: Map<string, EventCallback[]>;
  private touchStartX: number = 0;
  private touchStartY: number = 0;
  private readonly MIN_SWIPE_DISTANCE = 30; // 最小滑动距离（像素）

  constructor() {
    this.events = new Map();
    this.listen();
  }

  /**
   * 绑定事件监听器
   * @param event 事件名称
   * @param callback 回调函数
   */
  public on(event: string, callback: EventCallback): void {
    if (!this.events.has(event)) {
      this.events.set(event, []);
    }
    this.events.get(event)?.push(callback);
  }

  /**
   * 触发事件
   * @param event 事件名称
   * @param data 事件数据
   */
  public emit(event: string, data: Direction): void {
    const callbacks = this.events.get(event);
    if (callbacks) {
      callbacks.forEach((callback) => callback(data));
    }
  }

  /**
   * 监听用户输入
   */
  private listen(): void {
    // 键盘事件
    document.addEventListener('keydown', this.handleKeyDown.bind(this));

    // 触摸事件（移动端）
    document.addEventListener('touchstart', this.handleTouchStart.bind(this), { passive: false });
    document.addEventListener('touchmove', this.handleTouchMove.bind(this), { passive: false });
    document.addEventListener('touchend', this.handleTouchEnd.bind(this));

    // 重新开始按钮（绑定所有匹配的按钮）
    const restartButtons = document.querySelectorAll('.restart-button');
    restartButtons.forEach((button) => {
      button.addEventListener('click', this.handleRestart.bind(this));
    });
  }

  /**
   * 处理键盘按下事件
   * @param event 键盘事件
   */
  private handleKeyDown(event: KeyboardEvent): void {
    const key = event.key;
    let direction: Direction | null = null;

    if ((KEY_CODES.UP as readonly string[]).includes(key)) {
      direction = Direction.Up;
    } else if ((KEY_CODES.DOWN as readonly string[]).includes(key)) {
      direction = Direction.Down;
    } else if ((KEY_CODES.LEFT as readonly string[]).includes(key)) {
      direction = Direction.Left;
    } else if ((KEY_CODES.RIGHT as readonly string[]).includes(key)) {
      direction = Direction.Right;
    }

    if (direction) {
      event.preventDefault();
      this.emit('move', direction);
    }
  }

  /**
   * 处理触摸开始事件
   * @param event 触摸事件
   */
  private handleTouchStart(event: TouchEvent): void {
    if (event.touches.length > 1) {
      return; // 忽略多点触摸
    }

    this.touchStartX = event.touches[0]?.clientX ?? 0;
    this.touchStartY = event.touches[0]?.clientY ?? 0;
  }

  /**
   * 处理触摸移动事件
   * @param event 触摸事件
   */
  private handleTouchMove(event: TouchEvent): void {
    event.preventDefault(); // 防止页面滚动
  }

  /**
   * 处理触摸结束事件
   * @param event 触摸事件
   */
  private handleTouchEnd(event: TouchEvent): void {
    if (event.changedTouches.length === 0) {
      return;
    }

    const touchEndX = event.changedTouches[0]?.clientX ?? 0;
    const touchEndY = event.changedTouches[0]?.clientY ?? 0;

    const dx = touchEndX - this.touchStartX;
    const dy = touchEndY - this.touchStartY;

    const absDx = Math.abs(dx);
    const absDy = Math.abs(dy);

    // 判断是否为有效滑动
    if (Math.max(absDx, absDy) < this.MIN_SWIPE_DISTANCE) {
      return;
    }

    // 确定滑动方向
    let direction: Direction;
    if (absDx > absDy) {
      // 水平滑动
      direction = dx > 0 ? Direction.Right : Direction.Left;
    } else {
      // 垂直滑动
      direction = dy > 0 ? Direction.Down : Direction.Up;
    }

    this.emit('move', direction);
  }

  /**
   * 处理重新开始按钮点击
   */
  private handleRestart(): void {
    this.emit('restart', Direction.Up); // 传递一个占位值
  }

  /**
   * 移除所有事件监听器
   */
  public destroy(): void {
    document.removeEventListener('keydown', this.handleKeyDown.bind(this));
    document.removeEventListener('touchstart', this.handleTouchStart.bind(this));
    document.removeEventListener('touchmove', this.handleTouchMove.bind(this));
    document.removeEventListener('touchend', this.handleTouchEnd.bind(this));
  }
}
